import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {GetWalletResponse_Tab} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/service.types';
import {
  COLORS,
  Flyout,
  Modal,
  ModalDialog,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useRef, useState} from 'react';
import coinsWalletBackground from '../../../../features/Coins/assets/background-dark.png';
import {CouponInfoContentPopper} from '../../../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {COUPON_WALLET_INFO_ICON_CUSTOM_KEY} from '../../../../features/Coupons/CouponWallet/deprecated/highTierWallet/WalletBodyContentView';
import couponWalletBackground from '../../../../features/Coupons/assets/couponWalletBackground.svg';
import {WalletHeader} from '../WalletHeader/WalletHeader';
import {WalletTabs} from '../WalletHeader/WalletTabs';
import {WalletBody} from './WalletBody';
import {WalletContentProps} from './WalletContent';

export interface WalletContentViewProps extends WalletContentProps {
  tabs: GetWalletResponse_Tab[];
}

export const WalletContentView: React.FC<WalletContentViewProps> = ({
  tabs,
  activeTab,
  onTabClick,
  ContentPopperLayoutComponent,
  setSelectedProduct,
  ref,
}) => {
  const [infoIconContent, setInfoIconContent] = useState<CouponInfoSheet>();

  const handleInfoIconClick = (couponInfo?: CouponInfoSheet) => {
    setInfoIconContent(couponInfo);
  };

  const onCloseInfoSheet = useRef<VoidFunction>(undefined);

  const [headerHeight, setHeaderHeight] = useState<number | undefined>(
    undefined,
  );

  // as we are now  animating the "bottom sheet" out of view for
  // the full screen views, we need to disable overflow as otherwise the
  // user will be able to scroll into a lot of whitespace
  const disableOverflow =
    tabs.some(
      tab =>
        (tab.type?.$case === 'coupons' &&
          tab.type.coupons.header.type?.$case === 'couponsPausedSection') ||
        (tab.type?.$case === 'coupons' &&
          tab.type.coupons.header.type?.$case === 'emptyCouponSection'),
    ) && activeTab === 'coupons';

  return (
    <Wrapper disableOverflow={disableOverflow} ref={ref}>
      <CoinsBackground
        data-active={
          activeTab === 'coins' || activeTab === 'notificationCenter'
        }
      />
      <CouponsBackground data-active={activeTab === 'coupons'} />

      <WalletTabs
        tabs={tabs}
        onTabClick={onTabClick}
        activeTab={activeTab}
        showBackButton={ContentPopperLayoutComponent === Modal}
      />

      <WalletHeader
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={onTabClick}
        ContentPopperLayoutComponent={ContentPopperLayoutComponent}
        setSelectedProduct={setSelectedProduct}
        handleInfoIconClick={handleInfoIconClick}
        onCloseInfoSheet={onCloseInfoSheet}
        setHeaderHeight={setHeaderHeight}
      />

      <WalletBody
        tabs={tabs}
        activeTab={activeTab}
        ContentPopperLayoutComponent={ContentPopperLayoutComponent}
        setSelectedProduct={setSelectedProduct}
        handleInfoIconClick={handleInfoIconClick}
        headerHeight={headerHeight}
      />

      <StyledCouponInfoContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        data-testid="couponCard"
        couponInfo={infoIconContent}
        stateManagerCustomKey={COUPON_WALLET_INFO_ICON_CUSTOM_KEY}
        onClose={onCloseInfoSheet.current}
      />
    </Wrapper>
  );
};

const StyledCouponInfoContentPopper = styled(CouponInfoContentPopper)`
  &&& {
    z-index: ${Z_INDEXES.FLYOUT_FADE_ON_DRAWER};
  }
`;

const CoinsBackground = styled.div`
  opacity: 0;
  background-image: url(${coinsWalletBackground});
  background-size: 100%;
  transition: opacity 0.3s;
  position: absolute;
  inset: 0;

  &[data-active='true'] {
    opacity: 1;
    transition: opacity 0.1s;
  }
`;

const CouponsBackground = styled.div`
  opacity: 0;
  background-image: url(${couponWalletBackground});
  background-size: 100%;
  background-color: ${COLORS.PrimaryCoupon};

  transition: opacity 0.3s;
  position: absolute;
  inset: 0;

  &[data-active='true'] {
    opacity: 1;
    transition: opacity 0.1s;
  }
`;

const Wrapper = styled.div<{disableOverflow: boolean}>`
  position: relative;
  background-color: ${COLORS.White};
  overflow-x: hidden;
  overflow-y: ${props => (props.disableOverflow ? 'hidden' : 'auto')};
  height: 100%;
  width: 100%;
`;
